import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useModal, useDeleteConfirmModal, useRestrictedAccessModal } from '../../contexts/ModalContext';
import type { AccessMember } from '../stream/RestrictedAccessModal';
import type { AttentionItem } from '../../hooks/useAttention';

// Modal Components
import { GlobalCommandPalette } from '../agent/GlobalCommandPalette';
import { ExpertFinderModal } from '../agent/ExpertFinderModal';
import { KnowledgeInbox } from '../agent/KnowledgeInbox';
import { ProactiveSuggestions } from '../agent/ProactiveSuggestions';
import { ContextStoreBrowser } from '../context/ContextStoreBrowser';
import { ContextItemDetail } from '../context/ContextItemDetail';
import { UserPreferencesPage } from '../settings/UserPreferencesPage';
import { IntegrationsPage } from '../integrations/IntegrationsPage';
import { StreamSettingsPanel } from '../settings/StreamSettingsPanel';
import { ExportDocumentationModal } from '../export/ExportDocumentationModal';
import { BranchCreationModal } from '../branching/BranchCreationModal';
import { NewStreamModal } from './NewStreamModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { RestrictedAccessModal } from '../stream';
import { BotChat } from '../chat';
import { AttentionPanel } from './AttentionPanel';

interface ModalOrchestratorProps {
  // Stream data for modals that need it
  currentStreamName: string;
  selectedStreamId: string;
  // Attention items
  attentionItems: AttentionItem[];
  // Handlers
  onCreateStream: (name: string, description: string) => Promise<void>;
  onConfirmDelete: () => Promise<void>;
  onCreateBranch: (data: unknown) => void;
  onNavigateToAttentionItem: (streamId: string, nodeId: string) => void;
  // Access modal handlers
  onAddMember: (email: string, role: AccessMember['role']) => void;
  onRemoveMember: (memberId: string) => void;
  onUpdateMemberRole: (memberId: string, role: AccessMember['role']) => void;
}

export function ModalOrchestrator({
  currentStreamName,
  selectedStreamId,
  attentionItems,
  onCreateStream,
  onConfirmDelete,
  onCreateBranch,
  onNavigateToAttentionItem,
  onAddMember,
  onRemoveMember,
  onUpdateMemberRole,
}: ModalOrchestratorProps) {
  const { state, closeModal, isModalOpen } = useModal();
  const deleteConfirm = useDeleteConfirmModal();
  const restrictedAccess = useRestrictedAccessModal();

  return (
    <>
      {/* Command Palette */}
      <GlobalCommandPalette
        isOpen={isModalOpen('commandPalette')}
        onClose={() => closeModal('commandPalette')}
      />

      {/* Expert Finder */}
      <ExpertFinderModal
        isOpen={isModalOpen('expertFinder')}
        onClose={() => closeModal('expertFinder')}
      />

      {/* Proactive Suggestions */}
      <ProactiveSuggestions
        isOpen={isModalOpen('suggestions')}
        onClose={() => closeModal('suggestions')}
      />

      {/* Attention Panel */}
      <AttentionPanel
        isOpen={isModalOpen('attentionPanel')}
        onClose={() => closeModal('attentionPanel')}
        items={attentionItems}
        onNavigateToItem={onNavigateToAttentionItem}
      />

      {/* Knowledge Inbox */}
      <AnimatePresence>
        {isModalOpen('inbox') && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => closeModal('inbox')}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-12 bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-surface-600">
                <h2 className="text-lg font-semibold text-text-primary">
                  Knowledge Inbox
                </h2>
                <button
                  onClick={() => closeModal('inbox')}
                  className="text-text-muted hover:text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <KnowledgeInbox />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Context Store Browser */}
      <AnimatePresence>
        {isModalOpen('contextStore') && (
          <ContextStoreBrowser onClose={() => closeModal('contextStore')} />
        )}
      </AnimatePresence>

      {/* Context Item Detail */}
      <AnimatePresence>
        {(isModalOpen('contextItemDetail') && state.modalData.contextItemDetail) ? (
          <ContextItemDetail
            item={state.modalData.contextItemDetail as { name: string; path: string; content: string; lastModified: string; modifiedBy: string; usedBy: string[]; referencedIn: string[]; tags: string[] }}
            onClose={() => closeModal('contextItemDetail')}
          />
        ) : null}
      </AnimatePresence>

      {/* User Preferences */}
      <AnimatePresence>
        {isModalOpen('userPrefs') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-surface-900"
          >
            <button
              onClick={() => closeModal('userPrefs')}
              className="absolute top-4 right-4 text-text-muted hover:text-text-primary z-10"
            >
              <X className="w-6 h-6" />
            </button>
            <UserPreferencesPage />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Integrations */}
      <AnimatePresence>
        {isModalOpen('integrations') && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => closeModal('integrations')}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-12 bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50 overflow-auto"
            >
              <button
                onClick={() => closeModal('integrations')}
                className="absolute top-4 right-4 text-text-muted hover:text-text-primary"
              >
                <X className="w-6 h-6" />
              </button>
              <IntegrationsPage />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Stream Settings */}
      <StreamSettingsPanel
        isOpen={isModalOpen('streamSettings')}
        onClose={() => closeModal('streamSettings')}
        streamName={currentStreamName}
      />

      {/* Export Modal */}
      <ExportDocumentationModal
        isOpen={isModalOpen('exportModal')}
        onClose={() => closeModal('exportModal')}
      />

      {/* Branch Creation Modal */}
      <BranchCreationModal
        isOpen={isModalOpen('branchModal')}
        onClose={() => closeModal('branchModal')}
        sourceNode={currentStreamName || 'Current Node'}
        onCreate={onCreateBranch}
      />

      {/* New Stream Modal */}
      <NewStreamModal
        isOpen={isModalOpen('newStream')}
        onClose={() => closeModal('newStream')}
        onCreate={onCreateStream}
      />

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={deleteConfirm.close}
        onConfirm={onConfirmDelete}
        streamName={deleteConfirm.data?.streamName || ''}
      />

      {/* Restricted Access Modal */}
      <RestrictedAccessModal
        isOpen={restrictedAccess.isOpen}
        onClose={restrictedAccess.close}
        streamName={restrictedAccess.data?.streamName || ''}
        members={restrictedAccess.data?.members || []}
        onAddMember={onAddMember}
        onRemoveMember={onRemoveMember}
        onUpdateRole={onUpdateMemberRole}
      />

      {/* Bot Chat */}
      <AnimatePresence>
        {isModalOpen('botChat') && (
          <BotChat
            context={selectedStreamId ? 'stream' : 'global'}
            streamId={selectedStreamId}
            streamName={currentStreamName}
            onClose={() => closeModal('botChat')}
          />
        )}
      </AnimatePresence>
    </>
  );
}
